import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import artistService from "../services/artistService";

/**
 * An asynchronous thunk action creator that fetches a list of artists.
 * @param {{any}} data - The data to be passed to the API request.
 * @returns A promise that resolves to the response data from the API.
 */
export const artistListResponce = createAsyncThunk(
  "artist/list",
  async (data) => {
    const res = await artistService.getAllArtist(data);
    return res?.data;
  }
);

/**
 * Creates an async thunk action to create an artist.
 * @param {{any}} data - The data to be passed to the createArtist function.
 * @returns A promise that resolves to the response from the createArtist function.
 */
export const createArtistResponce = createAsyncThunk(
  "artist/create",
  async (data) => {
    const res = await artistService.createArtist(data);
    return res;
  }
);

/**
 * An asynchronous thunk action creator that updates an artist's response.
 * @param {{any}} data - The data to be passed to the updateArtist function.
 * @returns A promise that resolves to the updated artist response.
 */
export const updateArtistResponce = createAsyncThunk(
  "artist/update",
  async (data) => {
    const res = await artistService.updateArtist(data);
    return res;
  }
);

/**
 * Deletes an artist by their ID using an asynchronous thunk.
 * @param {{string}} id - The ID of the artist to delete.
 * @returns A promise that resolves to the response from the delete request.
 */
export const deleteArtistResponce = createAsyncThunk(
  "artist/delete",
  async (id) => {
    const res = await artistService.deleteArtist(id);
    return res;
  }
);

/**
 * Creates a Redux slice for managing artist-related state.
 * @param {{string}} name - The name of the slice.
 * @param {{object}} initialState - The initial state of the slice.
 * @param {{function}} extraReducers - A function that defines the extra reducers for the slice.
 * @returns A Redux slice object.
 */
const artistsSlice = createSlice({
  name: "artist",
  initialState: {
    artistResponce: [],
    createArtistResponce: [],
    createArtistError: "",
    updateArtistResponce: [],
    updateArtistError: "",
    deleteArtistResponce: [],
    deleteArtistError: "",
  },
  extraReducers: (builder) => {
    builder
      .addCase(artistListResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(artistListResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.artistResponce = action.payload;
      })
      .addCase(artistListResponce.rejected, (state) => {
        state.loading = false;
      });

    // create

    builder
      .addCase(createArtistResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(createArtistResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.createArtistResponce = action.payload;
      })
      .addCase(createArtistResponce.rejected, (state, action) => {
        state.loading = false;
        state.createArtistError = action?.error;
      });

    // update

    builder
      .addCase(updateArtistResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateArtistResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.updateArtistResponce = action.payload;
        state.artistResponce = state.artistResponce?.map((item) => {
          if (item?._id === action?.payload?.data?._id) {
            return {
              ...item,
              name: action?.payload?.data?.name,
              bio: action?.payload?.data?.bio,
              image: action?.payload?.data?.image,
            };
          }
          return item;
        });
      })
      .addCase(updateArtistResponce.rejected, (state, action) => {
        state.loading = false;
        state.updateArtistError = action?.error;
      });

    // delete

    builder
      .addCase(deleteArtistResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteArtistResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteArtistResponce = action.payload;
        state.artistResponce = state.artistResponce?.filter(
          (item) => item?._id !== action?.payload?.data?._id
        );
      })
      .addCase(deleteArtistResponce.rejected, (state, action) => {
        state.loading = false;
        state.deleteArtistError = action?.error;
      });
  },
});

export default artistsSlice.reducer;
